<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-flex lg12>
        <v-card>
          <v-card-title>
            Cấu hình bậc thưởng doanh số
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="openAddDialog">
              <v-icon left>add</v-icon>Thêm bậc
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-alert type="info" outlined dense>
              Hệ thống sẽ áp dụng bậc thưởng phù hợp nhất dựa trên tổng doanh số tháng của từng sale.
              Bậc có <b>MaxRevenue = trống</b> nghĩa là không giới hạn trên.
            </v-alert>
          </v-card-text>

          <v-data-table
            :headers="headers"
            :items="tiers"
            :loading="loading"
            hide-default-footer
            disable-pagination
            class="elevation-1"
          >
            <template v-slot:item.MinRevenue="{ item }">
              {{ item.MinRevenue | currency }}
            </template>
            <template v-slot:item.MaxRevenue="{ item }">
              {{ item.MaxRevenue != null ? $options.filters.currency(item.MaxRevenue) : '∞' }}
            </template>
            <template v-slot:item.CommissionRate="{ item }">
              {{ item.CommissionRate }}%
            </template>
            <template v-slot:item.MinBonus="{ item }">
              {{ item.MinBonus | currency }}
            </template>
            <template v-slot:item.MaxBonus="{ item }">
              {{ item.MaxBonus != null ? $options.filters.currency(item.MaxBonus) : '∞' }}
            </template>
            <template v-slot:item.MarginBonusRate="{ item }">
              {{ item.MarginBonusRate }}%
            </template>
            <template v-slot:item.IsActive="{ item }">
              <v-chip :color="item.IsActive ? 'success' : 'grey'" small dark>
                {{ item.IsActive ? 'Hoạt động' : 'Tắt' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon small class="mr-2" @click="openEditDialog(item)">edit</v-icon>
              <v-icon small color="red" @click="confirmDelete(item)">delete</v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Add / Edit dialog -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>{{ editingItem.ID ? 'Sửa bậc thưởng' : 'Thêm bậc thưởng' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-row>
              <v-col cols="6">
                <h-currency-input
                  v-model="editingItem.MinRevenue"
                  label="Doanh số tối thiểu (VNĐ)"
                  :decimal="0"
                  class="input-group--focused"
                  :rules="[v => validateNonNegative(v, true)]"
                ></h-currency-input>
              </v-col>
              <v-col cols="6">
                <h-currency-input
                  v-model="editingItem.MaxRevenue"
                  label="Doanh số tối đa (VNĐ, trống = không giới hạn)"
                  :decimal="0"
                  class="input-group--focused"
                  :disabled="isMaxRevenueUnlimited"
                  :rules="[v => isMaxRevenueUnlimited || validateNonNegative(v)]"
                ></h-currency-input>
                <v-checkbox
                  v-model="isMaxRevenueUnlimited"
                  label="Không giới hạn"
                  dense
                  hide-details
                  class="mt-1"
                ></v-checkbox>
              </v-col>
              <v-col cols="6">
                <h-currency-input
                  v-model="editingItem.CommissionRate"
                  label="Tỷ lệ thưởng doanh số (%)"
                  :decimal="2"
                  class="input-group--focused"
                  :rules="[v => validateNonNegative(v, true)]"
                ></h-currency-input>
              </v-col>
              <v-col cols="6">
                <h-currency-input
                  v-model="editingItem.MarginBonusRate"
                  label="Tỷ lệ thưởng chênh lệch (%)"
                  :decimal="2"
                  class="input-group--focused"
                  :rules="[v => validateNonNegative(v, true)]"
                ></h-currency-input>
              </v-col>
              <v-col cols="6">
                <h-currency-input
                  v-model="editingItem.MinBonus"
                  label="Thưởng tối thiểu (VNĐ)"
                  :decimal="0"
                  class="input-group--focused"
                  :rules="[v => validateNonNegative(v)]"
                ></h-currency-input>
              </v-col>
              <v-col cols="6">
                <h-currency-input
                  v-model="editingItem.MaxBonus"
                  label="Thưởng tối đa (VNĐ, trống = không giới hạn)"
                  :decimal="0"
                  class="input-group--focused"
                  :disabled="isMaxBonusUnlimited"
                  :rules="[v => isMaxBonusUnlimited || validateNonNegative(v)]"
                ></h-currency-input>
                <v-checkbox
                  v-model="isMaxBonusUnlimited"
                  label="Không giới hạn"
                  dense
                  hide-details
                  class="mt-1"
                ></v-checkbox>
              </v-col>
              <v-col cols="6">
                <v-switch
                  v-model="editingItem.IsActive"
                  :true-value="1" :false-value="0"
                  label="Hoạt động"
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Hủy</v-btn>
          <v-btn color="primary" :disabled="!formValid" @click="saveTier">Lưu</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Xác nhận xóa</v-card-title>
        <v-card-text>Bạn có chắc muốn xóa bậc thưởng này?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Hủy</v-btn>
          <v-btn color="red" dark @click="deleteTier">Xóa</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

const defaultItem = () => ({
  ID: null,
  MinRevenue: 0,
  MaxRevenue: null,
  CommissionRate: 2,
  MinBonus: 0,
  MaxBonus: null,
  MarginBonusRate: 40,
  IsActive: 1
});

export default {
  name: 'SaleCommissionTierConfig',
  data() {
    return {
      dialog: false,
      deleteDialog: false,
      formValid: false,
      isMaxRevenueUnlimited: true,
      isMaxBonusUnlimited: true,
      editingItem: defaultItem(),
      deletingId: null,
      headers: [
        { text: 'Doanh số từ (VNĐ)',     value: 'MinRevenue',      align: 'end' },
        { text: 'Doanh số đến (VNĐ)',     value: 'MaxRevenue',      align: 'end' },
        { text: 'Thưởng doanh số (%)',          value: 'CommissionRate',  align: 'end' },
        { text: 'Thưởng chênh lệch (%)',          value: 'MarginBonusRate', align: 'end' },
        { text: 'Thưởng tối thiểu (VNĐ)', value: 'MinBonus',       align: 'end' },
        { text: 'Thưởng tối đa (VNĐ)',    value: 'MaxBonus',        align: 'end' },
        { text: 'Trạng thái',             value: 'IsActive',        align: 'center' },
        { text: '',                       value: 'actions',         sortable: false }
      ]
    };
  },
  computed: {
    ...mapState({
      tiers:   state => state.saleCommissionConfig.tiers,
      loading: state => state.saleCommissionConfig.loading
    })
  },
  methods: {
    validateNonNegative(value, required = false) {
      if (value === null || value === undefined || value === '') {
        return required ? 'Không được để trống' : true;
      }
      const normalized = typeof value === 'string' ? value.replace(/,/g, '') : value;
      const parsed = Number(normalized);
      if (Number.isNaN(parsed)) return 'Giá trị không hợp lệ';
      return parsed >= 0 || 'Phải >= 0';
    },
    syncUnlimitedFlags() {
      this.isMaxRevenueUnlimited = this.editingItem.MaxRevenue == null;
      this.isMaxBonusUnlimited = this.editingItem.MaxBonus == null;
    },
    openAddDialog() {
      this.editingItem = defaultItem();
      this.syncUnlimitedFlags();
      this.dialog = true;
    },
    openEditDialog(item) {
      this.editingItem = { ...item };
      this.syncUnlimitedFlags();
      this.dialog = true;
    },
    saveTier() {
      if (!this.$refs.form.validate()) return;
      const payload = {
        ...this.editingItem,
        MaxRevenue: this.isMaxRevenueUnlimited ? null : this.editingItem.MaxRevenue,
        MaxBonus: this.isMaxBonusUnlimited ? null : this.editingItem.MaxBonus
      };
      const pk = this.editingItem.ID || null;
      this.$store.dispatch('saleCommissionConfig/saveTier', { tier: payload, pk });
      this.dialog = false;
    },
    confirmDelete(item) {
      this.deletingId = item.ID;
      this.deleteDialog = true;
    },
    deleteTier() {
      this.$store.dispatch('saleCommissionConfig/deleteTier', this.deletingId);
      this.deleteDialog = false;
    }
  },
  mounted() {
    this.$store.dispatch('saleCommissionConfig/getTiers');
    this.$root.$emit('REQUEST_ROOT_OPTION', {});
    this.$root.$on('REQUEST_REFRESH_DATA', () => this.$store.dispatch('saleCommissionConfig/getTiers'));
  },
  beforeDestroy() {
    this.$root.$off('REQUEST_REFRESH_DATA');
  }
};
</script>
